import React, { useCallback, useMemo, useState } from 'react';
import { RefreshControl, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { StatusBadge } from '../../components/StatusBadge';
import { ScreenHeader } from '../../components/ScreenHeader';
import { getKioskModuleCapabilities } from '../../config/kioskCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  deactivateKiosk,
  getKiosk,
  kioskStatusLabel,
} from '../../services/kioskAdminService';
import { KIOSK_STATUS, type KioskRecord } from '../../types/kiosk';
import { formatDateTime } from '../../utils/dateTime';
import { kioskAdminErrorMessage } from '../../utils/kioskErrors';
import { confirmSoftDelete } from '../../utils/confirmSoftDelete';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<RootStackParamList, 'KioskDetails'>;

function Field({
  label,
  value,
  palette,
}: {
  label: string;
  value: string | null;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.field}>
      <Text style={[styles.fieldLabel, { color: palette.textSecondary }]}>
        {label}
      </Text>
      <Text style={[styles.fieldValue, { color: palette.textPrimary }]}>
        {value || 'Not listed'}
      </Text>
    </View>
  );
}

export function KioskDetailsScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const capabilities = getKioskModuleCapabilities(auth.permissions);
  const { kioskId } = route.params;
  const [item, setItem] = useState<KioskRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const themed = useMemo(
    () => ({
      section: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        marginBottom: spacing.sm,
        marginTop: spacing.md,
      },
    }),
    [palette],
  );

  const load = useCallback(
    async (isRefresh = false) => {
      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const data = await getKiosk(kioskId);
        setItem(data);
        setError(null);
      } catch (caught) {
        setItem(null);
        setError(
          kioskAdminErrorMessage(caught, 'Unable to load this kiosk.'),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [kioskId],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  function confirmDeactivate() {
    if (!item) return;
    confirmSoftDelete({
      entityLabel: 'kiosk',
      recordName: item.kioskName,
      onConfirm: () => {
        deactivate().catch(() => undefined);
      },
    });
  }

  async function deactivate() {
    setBusy(true);
    try {
      const updated = await runOperation('delete', () => deactivateKiosk(kioskId), {
        failureDetail: caught =>
          kioskAdminErrorMessage(caught, 'Unable to deactivate kiosk.'),
      });
      setItem(updated);
    } catch {
      // runOperation already showed the "Delete failed" popup.
    } finally {
      setBusy(false);
    }
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.SETTINGS_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Kiosk"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading kiosk" />
        </AppScreen>
      ) : error && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Kiosk"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={() => load()} />
        </AppScreen>
      ) : item ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
            />
          }
        >
          <ScreenHeader
            title={item.kioskName}
            onBack={navigation.goBack}
          />
          <View style={styles.body}>
            <StatusBadge label={kioskStatusLabel(item.status)} />
            <Text style={themed.section}>Details</Text>
            <AppCard style={styles.card}>
              {capabilities.canDeactivateKiosk &&
              item.status !== KIOSK_STATUS.INACTIVE ? (
                <ConfigurationDeleteButton
                  entityLabel="kiosk"
                  loading={busy}
                  disabled={busy}
                  onPress={confirmDeactivate}
                />
              ) : null}
              <Field
                label="Device name"
                value={item.deviceName}
                palette={palette}
              />
              <Field
                label="Device identifier"
                value={item.deviceIdentifier}
                palette={palette}
              />
              <Field
                label="Location ID"
                value={item.locationId ? String(item.locationId) : null}
                palette={palette}
              />
              <Field
                label="Activated"
                value={item.activatedAt ? formatDateTime(item.activatedAt) : null}
                palette={palette}
              />
              <Field
                label="Last seen"
                value={item.lastSeenAt ? formatDateTime(item.lastSeenAt) : null}
                palette={palette}
              />
            </AppCard>

            <View style={styles.actions}>
              {capabilities.canManageKiosk &&
              item.status !== KIOSK_STATUS.INACTIVE ? (
                <AppButton
                  title="Generate activation QR"
                  onPress={() =>
                    navigation.navigate(routes.kioskActivationQr, {
                      kioskId: item.kioskId,
                    })
                  }
                  disabled={busy}
                />
              ) : null}
            </View>
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  body: { marginTop: spacing.lg, gap: spacing.md },
  card: { gap: spacing.sm },
  field: { gap: spacing.xs },
  fieldLabel: { ...typography.caption },
  fieldValue: { ...typography.body },
  actions: { gap: spacing.sm, marginTop: spacing.md },
});
